import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Building2,
  Download,
  LoaderCircle,
  Pencil,
  ShieldCheck,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { api } from "../../api";
import { t } from "../../i18n";
import { SettingsPage } from "../settings/ProfilePage";
import {
  OrganizationEditor,
  type EditableOrganization,
} from "../../components/organization/OrganizationEditor";
import { OrganizationPermissionsEditor } from "../../components/organization/OrganizationPermissionsEditor";
import { normalizeOrganizationPermissions } from "../../organization-permissions";
import {
  AuthenticationConnectionsEditor,
  type AuthenticationConnection,
} from "../../components/integrations/AuthenticationConnectionsEditor";
import { OrganizationInviteForm } from "../../components/organization/OrganizationInviteForm";
import {
  OrganizationNetworkPolicyEditor,
  type OrganizationNetworkPolicy,
} from "../../components/organization/OrganizationNetworkPolicyEditor";
import { OrganizationConsoleNav } from "../../components/organization/OrganizationConsoleNav";
import { OrganizationResourcesPanel } from "../../components/organization/OrganizationResourcesPanel";
import type { OrganizationCapabilities, User } from "@lumen/shared";

type Organization = {
  id: string;
  name: string;
  slug: string;
  state: string;
  locale: string;
  timezone: string;
  role: string;
};
type Member = {
  user: { id: string; name: string; email: string };
  membership: { role: string; state: string };
  permissions: string[] | null;
};
type Audit = {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  createdAt: string;
};
type ModelAccess = {
  id: string;
  name: string;
  enabled: boolean;
  defaultEffect: "allow" | "deny";
};
function organizationRoleLabel(role: string) {
  if (role === "owner") return t("Owner");
  if (role === "admin") return t("Administrator");
  if (role === "automation_manager") return t("Automation manager");
  if (role === "connection_manager") return t("Connection manager");
  if (role === "billing_admin") return t("Billing administrator");
  return t("Member");
}
export function OrganizationWorkspace({
  permissions,
  user,
  mode,
}: {
  permissions: string[];
  user: User;
  mode: "list" | "edit";
}) {
  const { tab = "overview" } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<{
    activeOrganizationId: string;
    items: Organization[];
  }>();
  const [members, setMembers] = useState<Member[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [modelAccess, setModelAccess] = useState<ModelAccess[]>([]);
  const [simulation, setSimulation] = useState("");
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [memberRole, setMemberRole] = useState("member");
  const [memberState, setMemberState] = useState("active");
  const [memberPermissions, setMemberPermissions] = useState<string[]>([]);
  const [advancedPermissions, setAdvancedPermissions] = useState(false);
  const [defaultPermissions, setDefaultPermissions] = useState<string[]>([]);
  const [personalProvidersEnabled, setPersonalProvidersEnabled] =
    useState(true);
  const [capabilities, setCapabilities] = useState<OrganizationCapabilities>(
    user.capabilities,
  );
  const [networkPolicy, setNetworkPolicy] =
    useState<OrganizationNetworkPolicy | null>(null);
  const [savingNetworkPolicy, setSavingNetworkPolicy] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [authConnections, setAuthConnections] = useState<
    AuthenticationConnection[]
  >([]);
  const [savingMember, setSavingMember] = useState(false);
  const [editingOrganization, setEditingOrganization] =
    useState<EditableOrganization | null>(null);
  const [savingOrganization, setSavingOrganization] = useState(false);
  useEffect(() => {
    void Promise.all([
      api<{ activeOrganizationId: string; items: Organization[] }>(
        "/organizations",
      ),
      api<Member[]>("/organization/members").catch(() => []),
      api<Audit[]>("/organization/audit-events").catch(() => []),
      api<ModelAccess[]>("/organization/model-access").catch(() => []),
      api<{ permissions: string[] }>("/organization/default-permissions").catch(
        () => ({ permissions: [] }),
      ),
      api<OrganizationCapabilities>("/organization/capabilities"),
      api<OrganizationNetworkPolicy>("/organization/network-policy").catch(
        () => null,
      ),
      api<AuthenticationConnection[]>("/organization/auth-connections").catch(
        () => [],
      ),
    ])
      .then(
        ([
          nextOrganizations,
          nextMembers,
          nextAudit,
          nextModelAccess,
          nextDefaults,
          nextCapabilities,
          nextNetworkPolicy,
          nextAuthConnections,
        ]) => {
          setOrganizations(nextOrganizations);
          setMembers(nextMembers);
          setAudit(nextAudit);
          setModelAccess(nextModelAccess);
          setDefaultPermissions(
            normalizeOrganizationPermissions(nextDefaults.permissions),
          );
          setPersonalProvidersEnabled(nextCapabilities.personalProviders);
          setCapabilities(nextCapabilities);
          setNetworkPolicy(nextNetworkPolicy);
          setAuthConnections(nextAuthConnections);
          if (mode === "edit") {
            const active = nextOrganizations.items.find(
              (item) => item.id === nextOrganizations.activeOrganizationId,
            );
            if (active) setEditingOrganization(active);
          }
        },
      )
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "Organization settings could not be loaded",
        ),
      );
  }, [mode]);
  const activate = async (id: string) => {
    await api(`/organizations/${id}/activate`, { method: "POST" });
    window.location.reload();
  };
  const scheduleDeletion = async () => {
    const active = organizations?.items.find(
      (item) => item.id === organizations.activeOrganizationId,
    );
    if (!active) return;
    const confirmation = prompt(
      t("Enter the organization slug to schedule deletion after retention:"),
    );
    if (!confirmation) return;
    await api("/organization/offboarding", {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    });
    window.location.reload();
  };
  const importOrganization = async (file: File) => {
    setImporting(true);
    setError("");
    try {
      const payload = JSON.parse(await file.text()) as unknown;
      const result = await api<{ conversations: number; messages: number }>(
        "/organization/import",
        { method: "POST", body: JSON.stringify(payload) },
      );
      window.alert(
        t("Imported {conversations} conversations and {messages} messages.")
          .replace("{conversations}", String(result.conversations))
          .replace("{messages}", String(result.messages)),
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t("Organization import failed"),
      );
    } finally {
      setImporting(false);
    }
  };
  const setModelDefault = async (
    model: ModelAccess,
    defaultEffect: "allow" | "deny",
  ) => {
    await api(`/organization/resource-policies/model/${model.id}`, {
      method: "PUT",
      body: JSON.stringify({ defaultEffect }),
    });
    setModelAccess((current) =>
      current.map((item) =>
        item.id === model.id ? { ...item, defaultEffect } : item,
      ),
    );
  };
  const setModelRule = async (
    modelId: string,
    subjectId: string,
    effect: "allow" | "deny",
  ) => {
    await api(
      `/organization/resource-rules/model/${modelId}/user/${subjectId}`,
      { method: "PUT", body: JSON.stringify({ effect }) },
    );
  };
  const saveNetworkPolicy = async (
    outboundHosts: string[] | null,
    providerHosts: string[] | null,
  ) => {
    setSavingNetworkPolicy(true);
    setError("");
    try {
      const updated = await api<{
        outboundHosts: string[] | null;
        providerHosts: string[] | null;
        effectiveOutboundHosts: string[] | null;
        effectiveProviderHosts: string[];
      }>("/organization/network-policy", {
        method: "PUT",
        body: JSON.stringify({ outboundHosts, providerHosts }),
      });
      setNetworkPolicy((current) =>
        current
          ? {
              ...current,
              outboundHosts: updated.outboundHosts,
              providerHosts: updated.providerHosts,
              effectiveOutboundHosts: updated.effectiveOutboundHosts,
              effectiveProviderHosts: updated.effectiveProviderHosts,
            }
          : current,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t("Network policy could not be saved"),
      );
    } finally {
      setSavingNetworkPolicy(false);
    }
  };
  const simulate = async (modelId: string, userId: string) => {
    const result = await api<{ allowed: boolean; reason: string }>(
      "/organization/policy-simulator",
      {
        method: "POST",
        body: JSON.stringify({
          userId,
          resourceType: "model",
          resourceId: modelId,
        }),
      },
    );
    setSimulation(
      `${result.allowed ? t("Allowed") : t("Denied")}: ${result.reason}`,
    );
  };
  const editMember = (member: Member) => {
    setEditingMember(member);
    setMemberRole(member.membership.role);
    setMemberState(member.membership.state);
    setMemberPermissions(
      normalizeOrganizationPermissions(member.permissions ?? []),
    );
    setAdvancedPermissions(false);
  };
  const saveDefaultPermissions = async () => {
    setSavingDefaults(true);
    try {
      await api("/organization/default-permissions", {
        method: "PUT",
        body: JSON.stringify({
          permissions: normalizeOrganizationPermissions(defaultPermissions),
        }),
      });
    } finally {
      setSavingDefaults(false);
    }
  };
  const updateNativePolicy = async (
    connection: AuthenticationConnection,
    policy: NonNullable<AuthenticationConnection["nativePolicy"]>,
  ) => {
    await api(`/organization/auth-connections/${connection.id}/native-policy`, {
      method: "PATCH",
      body: JSON.stringify(policy),
    });
    setAuthConnections((current) =>
      current.map((item) =>
        item.id === connection.id ? { ...item, nativePolicy: policy } : item,
      ),
    );
  };
  const createAuthenticationConnection = async (
    input: Record<string, unknown>,
  ) => {
    const created = await api<AuthenticationConnection>(
      "/organization/auth-connections",
      { method: "POST", body: JSON.stringify(input) },
    );
    setAuthConnections((current) => [...current, created]);
  };
  const saveMember = async () => {
    if (!editingMember) return;
    setSavingMember(true);
    setError("");
    try {
      await api(`/organization/members/${editingMember.user.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          role: memberRole,
          state: memberState,
          permissions: normalizeOrganizationPermissions(memberPermissions),
        }),
      });
      const refreshed = await api<Member[]>("/organization/members");
      setMembers(refreshed);
      setEditingMember(null);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Member settings could not be saved",
      );
    } finally {
      setSavingMember(false);
    }
  };
  const inviteMember = async (input: {
    name: string;
    email: string;
    role: string;
  }) => {
    await api("/organization/members/invite", {
      method: "POST",
      body: JSON.stringify(input),
    });
    setMembers(await api<Member[]>("/organization/members"));
  };
  const saveOrganization = async () => {
    if (!editingOrganization) return;
    setSavingOrganization(true);
    setError("");
    try {
      const updated = await api<Organization>("/organization", {
        method: "PATCH",
        body: JSON.stringify({
          name: editingOrganization.name,
          slug: editingOrganization.slug,
          locale: editingOrganization.locale,
          timezone: editingOrganization.timezone,
        }),
      });
      setOrganizations((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === updated.id ? { ...item, ...updated } : item,
              ),
            }
          : current,
      );
      setEditingOrganization(updated);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Organization could not be saved",
      );
    } finally {
      setSavingOrganization(false);
    }
  };
  return (
    <SettingsPage
      title={t("Organization")}
      description={
        mode === "edit"
          ? t("Edit this organization's settings and access.")
          : t(
              "Switch organizations and review members, roles, and security activity.",
            )
      }
    >
      {error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {!organizations && !error && (
        <div className="grid min-h-64 place-items-center">
          <LoaderCircle className="animate-spin text-stone-400" />
        </div>
      )}
      {organizations && (
        <div className="space-y-6">
          {mode === "edit" && editingOrganization && (
            <>
              <div className="flex items-start justify-between gap-4 rounded-xl border border-stone-200 bg-white p-4 dark:border-white/10 dark:bg-white/3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-bold">{editingOrganization.name}</h2>
                    <span className="rounded-full bg-lumen-100 px-2 py-1 text-xs font-semibold text-lumen-800 dark:bg-lumen-900/40 dark:text-lumen-200">
                      {organizationRoleLabel(
                        editingOrganization.role ?? "member",
                      )}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-stone-500">
                    {editingOrganization.slug} · {t("Organization settings")}
                  </p>
                </div>
                <Link className="button" to="/settings/organization">
                  {t("Back to organizations")}
                </Link>
              </div>
              <OrganizationConsoleNav
                basePath="/settings/organization/edit"
                activeTab={tab}
                groups={[
                  {
                    label: "Organization",
                    items: [
                      { key: "overview", label: "Overview" },
                      { key: "members", label: "People" },
                      { key: "resources", label: "Resources" },
                    ],
                  },
                  {
                    label: "Access & security",
                    items: [
                      { key: "access", label: "Access" },
                      ...(permissions.includes("policies:manage")
                        ? [{ key: "security", label: "Network & security" }]
                        : []),
                      { key: "authentication", label: "Authentication" },
                    ],
                  },
                  {
                    label: "Operations",
                    items: [
                      ...(permissions.includes("usage:read")
                        ? [
                            {
                              key: "limits",
                              label: "Usage & limits",
                              href: "/settings/usage",
                            },
                          ]
                        : []),
                    ],
                  },
                  {
                    label: "Commercial & lifecycle",
                    items: [
                      ...(permissions.includes("billing:read")
                        ? [
                            {
                              key: "billing",
                              label: "Billing",
                              href: "/settings/billing",
                            },
                          ]
                        : []),
                      { key: "data", label: "Data" },
                      { key: "audit", label: "Audit" },
                    ],
                  },
                ]}
              />
            </>
          )}
          {mode === "list" && (
            <section className="panel p-5">
              <div className="mb-4 flex items-center gap-2">
                <Building2 size={19} />
                <h2 className="font-bold">{t("Your organizations")}</h2>
              </div>
              <div className="space-y-2">
                {organizations.items.map((organization) => {
                  const active =
                    organization.id === organizations.activeOrganizationId;
                  return (
                    <button
                      key={organization.id}
                      aria-current={active ? "true" : undefined}
                      className={`flex w-full items-center justify-between rounded-xl border p-3 text-left ${
                        active
                          ? "cursor-default border-lumen-400 bg-lumen-50 text-stone-900 dark:border-lumen-500 dark:bg-lumen-900/40 dark:text-stone-100"
                          : "border-stone-200 bg-white text-stone-900 hover:bg-stone-50 dark:border-white/10 dark:bg-white/3 dark:text-stone-100 dark:hover:bg-white/7"
                      }`}
                      onClick={
                        active
                          ? undefined
                          : () => void activate(organization.id)
                      }
                    >
                      <span>
                        <span className="block font-medium">
                          {organization.name}
                        </span>
                        <span className="text-xs text-stone-600 dark:text-stone-300">
                          {organizationRoleLabel(organization.role)}
                        </span>
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          active
                            ? "bg-lumen-100 text-lumen-800 dark:bg-lumen-900/60 dark:text-lumen-200"
                            : "bg-stone-100 text-stone-600 dark:bg-white/10 dark:text-stone-300"
                        }`}
                      >
                        {active
                          ? t("Current organization")
                          : t("Switch organization")}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex justify-end">
                <div className="flex flex-wrap justify-end gap-2">
                  <Link className="button" to="/settings/organization/new">
                    {t("Create organization")}
                  </Link>
                  <Link
                    className="button"
                    to="/settings/organization/edit/overview"
                  >
                    <Pencil size={14} /> {t("Edit organization")}
                  </Link>
                </div>
              </div>
            </section>
          )}
          {mode === "edit" && tab === "overview" && editingOrganization && (
            <div className="space-y-5">
              <OrganizationEditor
                value={editingOrganization}
                saving={savingOrganization}
                onChange={setEditingOrganization}
                onSave={() => void saveOrganization()}
                onCancel={() => navigate("/settings/organization")}
              />
              <section className="panel p-5">
                <h2 className="font-bold">{t("Organization setup")}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "Finish setup from focused areas so members, resources, and security policies stay easy to find.",
                  )}
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    ["members", "People", "Invite members and assign roles."],
                    [
                      "resources",
                      "Resources",
                      "Manage shared skills, agents, tools, and workspaces.",
                    ],
                    ...(permissions.includes("policies:manage")
                      ? [
                          [
                            "security",
                            "Security policy",
                            "Control outbound destinations and access policy.",
                          ],
                        ]
                      : []),
                    [
                      "authentication",
                      "Authentication",
                      "Configure how members sign in.",
                    ],
                    [
                      "data",
                      "Data",
                      "Export or schedule deletion of organization data.",
                    ],
                    [
                      "audit",
                      "Audit log",
                      "Review security and administration activity.",
                    ],
                  ].map(([target, title, description]) => (
                    <Link
                      key={target}
                      className="rounded-xl border border-stone-200 p-4 text-left hover:border-lumen-400 dark:border-white/10 dark:hover:border-lumen-500"
                      to={`/settings/organization/edit/${target}`}
                    >
                      <strong className="block text-sm">{t(title)}</strong>
                      <span className="mt-1 block text-xs text-stone-500">
                        {t(description)}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          )}
          {mode === "edit" && tab === "resources" && organizations && (
            <OrganizationResourcesPanel
              permissions={permissions}
              capabilities={capabilities}
              organizationId={organizations.activeOrganizationId}
              user={user}
            />
          )}
          {mode === "edit" && tab === "members" && (
            <section className="panel overflow-hidden">
              <div className="flex items-center gap-2 border-b border-stone-200 p-5 dark:border-white/10">
                <Users size={19} />
                <h2 className="font-bold">{t("Members")}</h2>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {members.map((member) => (
                  <div
                    key={member.user.id}
                    className="flex items-center justify-between gap-3 p-4"
                  >
                    <div>
                      <div className="font-medium">{member.user.name}</div>
                      <div className="text-xs text-stone-500">
                        {member.user.email}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right text-xs">
                        <div>
                          {organizationRoleLabel(member.membership.role)}
                        </div>
                        <div className="text-stone-500">
                          {member.membership.state}
                        </div>
                      </div>
                      <button
                        className="button"
                        disabled={!permissions.includes("users:write")}
                        onClick={() => editMember(member)}
                      >
                        <Pencil size={14} /> {t("Manage")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {permissions.includes("users:write") && (
                <div className="p-4">
                  <OrganizationInviteForm onInvite={inviteMember} />
                </div>
              )}
            </section>
          )}
          {mode === "edit" && tab === "members" && editingMember && (
            <section className="panel p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold">{t("Manage member")}</h2>
                  <p className="text-sm text-stone-500">
                    {editingMember.user.name} · {editingMember.user.email}
                  </p>
                </div>
                <button
                  className="button"
                  onClick={() => setEditingMember(null)}
                >
                  {t("Cancel")}
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label>
                  <span className="label">{t("Organization role")}</span>
                  <select
                    className="field"
                    value={memberRole}
                    onChange={(event) => setMemberRole(event.target.value)}
                  >
                    <option value="owner">{t("Owner")}</option>
                    <option value="admin">{t("Administrator")}</option>
                    <option value="automation_manager">
                      {t("Automation manager")}
                    </option>
                    <option value="connection_manager">
                      {t("Connection manager")}
                    </option>
                    <option value="member">{t("Member")}</option>
                    <option value="billing_admin">
                      {t("Billing administrator")}
                    </option>
                  </select>
                </label>
                <label>
                  <span className="label">{t("Account state")}</span>
                  <select
                    className="field"
                    value={memberState}
                    onChange={(event) => setMemberState(event.target.value)}
                  >
                    <option value="invited">{t("Invited")}</option>
                    <option value="active">{t("Active")}</option>
                    <option value="suspended">{t("Suspended")}</option>
                  </select>
                </label>
              </div>
              <div className="mt-5 flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-semibold">{t("Access role")}</h3>
                  <p className="mt-1 text-sm text-stone-500">
                    {t(
                      "Built-in roles are the recommended way to grant access. Custom permissions are an advanced override and remain organization-scoped.",
                    )}
                  </p>
                </div>
                <button
                  className="button text-xs"
                  type="button"
                  onClick={() => setAdvancedPermissions((value) => !value)}
                >
                  {advancedPermissions
                    ? t("Hide advanced")
                    : t("Show advanced")}
                </button>
              </div>
              {advancedPermissions ? (
                <div className="mt-3">
                  <OrganizationPermissionsEditor
                    value={memberPermissions}
                    onChange={setMemberPermissions}
                    personalProvidersEnabled={personalProvidersEnabled}
                  />
                </div>
              ) : null}
              <div className="mt-4 flex justify-end">
                <button
                  className="button-primary"
                  disabled={savingMember}
                  onClick={() => void saveMember()}
                >
                  {savingMember && (
                    <LoaderCircle className="animate-spin" size={15} />
                  )}
                  {t("Save member")}
                </button>
              </div>
            </section>
          )}
          {mode === "edit" && tab === "members" && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Default permissions")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "Permissions initially assigned to ordinary members. Individual member permissions can be changed separately.",
                )}
              </p>
              <div className="mt-4">
                <OrganizationPermissionsEditor
                  value={defaultPermissions}
                  onChange={
                    permissions.includes("users:write")
                      ? setDefaultPermissions
                      : () => undefined
                  }
                  personalProvidersEnabled={personalProvidersEnabled}
                />
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="button-primary"
                  disabled={
                    savingDefaults || !permissions.includes("users:write")
                  }
                  onClick={() => void saveDefaultPermissions()}
                >
                  {t("Save default permissions")}
                </button>
              </div>
            </section>
          )}
          {mode === "edit" &&
            tab === "authentication" &&
            permissions.includes("organization:auth:manage") && (
              <AuthenticationConnectionsEditor
                connections={authConnections}
                onCreate={createAuthenticationConnection}
                onNativePolicyChange={(connection, policy) =>
                  void updateNativePolicy(connection, policy)
                }
              />
            )}
          {mode === "edit" && tab === "security" && networkPolicy && (
            <OrganizationNetworkPolicyEditor
              policy={networkPolicy}
              saving={savingNetworkPolicy}
              canEdit={false}
              onReset={() => void saveNetworkPolicy(null, null)}
              onChange={setNetworkPolicy}
              onSave={() =>
                void saveNetworkPolicy(
                  networkPolicy.outboundHosts,
                  networkPolicy.providerHosts,
                )
              }
            />
          )}
          {mode === "edit" && tab === "access" && (
            <section className="panel mb-5 flex flex-wrap items-center justify-between gap-4 p-5">
              <div>
                <h2 className="font-bold">{t("Custom models")}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "Create routing and fallback models shared with this organization.",
                  )}
                </p>
              </div>
              {(permissions.includes("models:write") ||
                permissions.includes("organization:manage")) && (
                <Link
                  className="button"
                  to="/settings/custom-models?scope=organization"
                >
                  {t("Manage custom models")}
                </Link>
              )}
            </section>
          )}
          {mode === "edit" && tab === "access" && (
            <section className="panel overflow-hidden">
              <div className="border-b border-stone-200 p-5 dark:border-white/10">
                <h2 className="font-bold">{t("Model access policy")}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "Organization defaults can be tightened for individual users. Deny always overrides allow.",
                  )}
                </p>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {modelAccess.map((model) => (
                  <div key={model.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <strong>{model.name}</strong>
                      <select
                        className="field w-auto"
                        value={model.defaultEffect}
                        disabled={
                          !model.enabled ||
                          !permissions.includes("policies:manage")
                        }
                        onChange={(event) =>
                          void setModelDefault(
                            model,
                            event.target.value as "allow" | "deny",
                          )
                        }
                      >
                        <option value="allow">{t("Allow by default")}</option>
                        <option value="deny">{t("Deny by default")}</option>
                      </select>
                    </div>
                    {!model.enabled && (
                      <p className="mt-1 text-xs text-amber-600">
                        {t(
                          "Site entitlement disabled; organization policy cannot relax this restriction.",
                        )}
                      </p>
                    )}
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {(["allow", "deny"] as const).map((effect) => (
                        <select
                          key={effect}
                          className="field"
                          defaultValue=""
                          onChange={(event) => {
                            const id = event.target.value;
                            if (id) void setModelRule(model.id, id, effect);
                            event.target.value = "";
                          }}
                        >
                          <option value="">
                            {effect === "allow"
                              ? t("Grant to…")
                              : t("Deny to…")}
                          </option>
                          {members.map((member) => (
                            <option
                              key={`${effect}-u-${member.user.id}`}
                              value={member.user.id}
                            >
                              {member.user.name}
                            </option>
                          ))}
                        </select>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {members.slice(0, 5).map((member) => (
                        <button
                          key={member.user.id}
                          className="button text-xs"
                          onClick={() =>
                            void simulate(model.id, member.user.id)
                          }
                        >
                          {t("Simulate")}: {member.user.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {!modelAccess.length && (
                  <p className="p-4 text-sm text-stone-400">
                    {t("No models are entitled to this organization.")}
                  </p>
                )}
              </div>
              {simulation && (
                <p className="border-t border-stone-200 p-4 text-sm dark:border-white/10">
                  {simulation}
                </p>
              )}
            </section>
          )}
          {mode === "edit" && tab === "data" && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Export and offboarding")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "Download organization data before scheduling retention-delayed deletion.",
                )}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <a className="button" href="/api/organization/export">
                  <Download size={15} /> {t("Download export")}
                </a>
                <label className="button cursor-pointer">
                  <Upload size={15} />{" "}
                  {importing ? t("Importing…") : t("Import export file")}
                  <input
                    className="hidden"
                    type="file"
                    accept="application/json,.json"
                    disabled={importing}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void importOrganization(file);
                      event.target.value = "";
                    }}
                  />
                </label>
                <button
                  className="button text-red-600"
                  onClick={() => void scheduleDeletion()}
                >
                  <Trash2 size={15} /> {t("Schedule deletion")}
                </button>
              </div>
            </section>
          )}
          {mode === "edit" && tab === "audit" && (
            <section className="panel overflow-hidden">
              <div className="flex items-center gap-2 border-b border-stone-200 p-5 dark:border-white/10">
                <ShieldCheck size={19} />
                <h2 className="font-bold">{t("Recent security activity")}</h2>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {audit.slice(0, 25).map((event) => (
                  <div
                    key={event.id}
                    className="flex justify-between gap-3 p-3 text-sm"
                  >
                    <span>
                      {event.action} · {event.targetType}
                    </span>
                    <time className="text-xs text-stone-500">
                      {new Date(event.createdAt).toLocaleString()}
                    </time>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </SettingsPage>
  );
}

export { OrganizationPage } from "./OrganizationListPage";
export { OrganizationEditPage } from "./OrganizationEditPage";
export { OrganizationCreatePage } from "./OrganizationCreatePage";
